import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";

const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/;
const MATRICULA_REGEX = /^[A-Za-z0-9]{6,10}$/;
const BRACELET_CODE_REGEX = /^[1-9]\d{0,6}$/;
export const HEALTH_CONDITION_OPTIONS = [
    'Ninguna',
    'Movilidad reducida',
    'Discapacidad visual',
    'Discapacidad auditiva',
    'Condición médica relevante',
    'Alergias alimentarias',
    'Alergias a medicamentos',
    'Requiere dieta especial',
    'Requiere asistencia personal',
    'Vendrá acompañado por un perro guía',
    'Otra (especificar)',
] as const;
export const HEALTH_NONE_OPTION = 'Ninguna';
export const HEALTH_OTHER_OPTION = 'Otra (especificar)';
export const BLOOD_TYPE_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'No lo sé'] as const;

// Constantes sincronizadas con backend/base de datos
export const TIPOS_ASISTENTE = [
    { value: "estudiante-unach", label: "Estudiante UNACH" },
    { value: "estudiante-externo", label: "Estudiante externo" },
    { value: "publico-general", label: "Publico General" },
] as const;

export const TIPOS_PONENTE = [
    { value: "docente-externo", label: "Docente/Investigador externo" },
    { value: "posgrado-externo", label: "Estudiante de posgrado externo" },
    { value: "pregrado-externo", label: "Tesista de pregrado externo" },
    { value: "docente-unach", label: "Docente/Investigador UNACH" },
    { value: "posgrado-unach", label: "Estudiante de posgrado UNACH" },
    { value: "pregrado-unach", label: "Estudiante de pregrado UNACH" },
] as const;

const GENERAL_PARTICIPATION_MODES = ["GENERAL_CON_KIT", "GENERAL_SIN_KIT"] as const;

const coautorSchema = z.object({
    nombre: z.string().min(2, "El nombre es muy corto"),
    correo: z.string().email("Correo invalido"),
    institucion: z.string().min(2, "Institucion requerida"),
});

const phoneSchema = z.string()
    .max(50, "Longitud excesiva")
    .transform((str) => str.replace(/[^0-9+]/g, ""))
    .superRefine((val, ctx) => {
        if (!val) return;

        const plusCount = (val.match(/\+/g) || []).length;
        if (plusCount > 1 || (plusCount === 1 && !val.startsWith("+"))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Formato de telefono invalido.",
            });
            return;
        }

        if (!val.startsWith("+")) {
            if (val.length < 7 || val.length > 15) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "El telefono debe contener entre 7 y 15 digitos.",
                });
            }
            return;
        }

        if (!isValidPhoneNumber(val)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Numero internacional invalido para el pais.",
            });
        }
    });

const healthDataSchema = z.object({
    conditions: z.array(z.enum(HEALTH_CONDITION_OPTIONS)).min(1, "Debes seleccionar al menos una opcion"),
    detailsImportant: z.string().trim().max(500, "Los detalles importantes no deben exceder 500 caracteres").optional().or(z.literal("")),
    otherDetails: z.string().trim().max(500, "La especificacion de 'Otra' no debe exceder 500 caracteres").optional().or(z.literal("")),
    emergencyContactName: z.string().trim().max(200, "El nombre del contacto no debe exceder 200 caracteres").optional().or(z.literal("")),
    emergencyContactPhone: phoneSchema.optional(),
    bloodType: z.enum(BLOOD_TYPE_OPTIONS).optional(),
    medicalAuthorization: z.boolean().optional(),
}).superRefine((data, ctx) => {
    const uniqueConditions = new Set(data.conditions);
    const hasNone = uniqueConditions.has(HEALTH_NONE_OPTION);
    const hasOther = uniqueConditions.has(HEALTH_OTHER_OPTION);
    const hasSpecialNeeds = Array.from(uniqueConditions).some((condition) => condition !== HEALTH_NONE_OPTION);

    if (hasNone && uniqueConditions.size > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conditions"],
            message: "La opcion 'Ninguna' no puede combinarse con otras selecciones",
        });
    }

    if (hasOther && !data.otherDetails?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["otherDetails"],
            message: "Debes especificar la opcion 'Otra (especificar)'",
        });
    }

    if (!hasOther && data.otherDetails?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["otherDetails"],
            message: "Solo puedes capturar 'Otra' cuando esa opcion esta seleccionada",
        });
    }

    if (hasSpecialNeeds) {
        if (!data.emergencyContactName?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["emergencyContactName"],
                message: "El nombre del contacto de emergencia es obligatorio",
            });
        }

        if (!data.emergencyContactPhone?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["emergencyContactPhone"],
                message: "El telefono del contacto de emergencia es obligatorio",
            });
        }

        if (!data.bloodType) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["bloodType"],
                message: "El tipo de sangre es obligatorio",
            });
        }

        if (typeof data.medicalAuthorization !== "boolean") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["medicalAuthorization"],
                message: "Debes indicar si autorizas asistencia medica en caso necesario",
            });
        }
    }
});

const baseRegistrationSchema = z.object({
    eventId: z.string().uuid("Seleccione un evento valido"),
    isExtranjero: z.boolean(),
    matricula: z.preprocess(
        (value) => typeof value === "string" ? value.trim() : value,
        z.string().regex(MATRICULA_REGEX, "La matricula debe contener entre 6 y 10 caracteres alfanumericos").optional().or(z.literal(""))
    ),
    participationMode: z.enum(["OYENTE_UNACH", "ASISTENTE", ...GENERAL_PARTICIPATION_MODES]).optional(),
    braceletCode: z.preprocess(
      (value) => (typeof value === "string" ? value.trim() : value),
      z.string().regex(BRACELET_CODE_REGEX, "El código de pulsera no debe tener cero inicial.").optional().or(z.literal(""))
    ),
    curp: z.preprocess(
        (value) => typeof value === "string" ? value.trim().toUpperCase() : value,
        z.string().regex(CURP_REGEX, "Ingresa una CURP valida").optional().or(z.literal(""))
    ),

    nombre: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
    apellidoPaterno: z.string().min(2, "El apellido paterno es obligatorio"),
    apellidoMaterno: z.string().min(2, "El apellido materno es obligatorio"),
    edad: z.coerce.number().min(15, "Debes tener al menos 15 anios").max(100, "Edad no valida"),
    genero: z.enum(["mujer", "hombre", "no-binario", "prefiero-no-decir", "otro"], {
        errorMap: () => ({ message: "Selecciona una opcion valida" }),
    }),
    correo: z.string().email("Ingresa un correo valido"),
    telefono: phoneSchema.optional(),

    pais: z.string().min(1, "Selecciona un pais"),
    estado: z.string().min(1, "Selecciona un estado"),
    ciudad: z.string().min(1, "Selecciona o escribe una ciudad"),
    healthData: healthDataSchema,

    institucion: z.string().min(2, "Institucion requerida"),

    comoSeEntero: z.string().min(1, "Selecciona una opcion"),
    aceptaTerminos: z.literal(true, {
        errorMap: () => ({ message: "Debes aceptar los terminos y condiciones" }),
    }),
});

export const attendeeSchemaV2 = baseRegistrationSchema.extend({
    role: z.literal("ATTENDEE"),
    tipoParticipante: z.string({ required_error: "Selecciona tu tipo de participacion" }),
}).superRefine((data, ctx) => {
    if (!data.isExtranjero && !data.curp) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["curp"],
            message: "La CURP es obligatoria para participantes nacionales",
        });
    }

    if (data.participationMode === "OYENTE_UNACH" && !data.matricula) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["matricula"],
            message: "La matricula es obligatoria para oyente UNACH.",
        });
    }

    const requiresGeneralMode =
        !data.isExtranjero &&
        (data.tipoParticipante === "estudiante-externo" || data.tipoParticipante === "publico-general");

    if (requiresGeneralMode) {
        if (!data.participationMode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["participationMode"],
                message: "Selecciona la modalidad general del asistente.",
            });
            return;
        }

        if (!GENERAL_PARTICIPATION_MODES.includes(data.participationMode as typeof GENERAL_PARTICIPATION_MODES[number])) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["participationMode"],
                message: "La modalidad seleccionada no es valida para el flujo normal de asistente.",
            });
        }
    }
});

export const speakerSchemaV2 = baseRegistrationSchema.extend({
    role: z.literal("SPEAKER"),
    tipoParticipante: z.string({ required_error: "Selecciona tu tipo de participacion" }),

    telefono: phoneSchema.refine((val) => val.length > 0, "Telefono obligatorio para ponentes"),
    area: z.string().min(2, "Area de adscripcion requerida"),

    tituloContribucion: z.string()
        .min(5, "Titulo muy corto")
        .refine(val => val.split(' ').length <= 25, "El titulo no debe exceder 25 palabras"),

    resumen: z.string()
        .min(50, "El resumen es muy corto (minimo 50 caracteres)")
        .max(2000, "El resumen excede el limite"),

    coautores: z.array(coautorSchema).max(3, "Maximo 3 coautores").optional(),

    documento: z.any()
        .refine((file) => file instanceof FileList ? file.length > 0 : !!file, "El archivo de la ponencia es obligatorio")
}).superRefine((data, ctx) => {
    if (!data.isExtranjero && !data.curp) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["curp"],
            message: "La CURP es obligatoria para participantes nacionales",
        });
    }
});

export type RegistrationFormV2 = z.infer<typeof attendeeSchemaV2> | z.infer<typeof speakerSchemaV2>;

